import { useEffect, useState } from 'react';
import { isValidPhoneNumber, getCountries, getCountryCallingCode } from 'libphonenumber-js';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function WaitlistModal({ onClose }) {
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!firstName.trim()) errs.first_name = 'First name is required';
    if (!email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) errs.email = 'Enter a valid email';
    if (Object.keys(errs).length) {
      setErrors(errs);
      const labels = { first_name: 'First Name', email: 'Email Address' };
      const missing = Object.entries(errs).map(([k, v]) => `${labels[k] || k}: ${v}`);
      alert(`Please fix the following:\n\n${missing.join('\n')}`);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ctfd/waitlist`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ first_name: firstName.trim(), email: email.trim() })
      });
      if (res.ok) setSubmitted(true);
    } catch { /* noop */ }
    setLoading(false);
  };

  return (
    <div className="ctfd-modal-overlay" onClick={onClose} data-testid="waitlist-modal-overlay">
      <div className="ctfd-modal" onClick={e => e.stopPropagation()} data-testid="waitlist-modal">
        <button className="ctfd-modal-close" onClick={onClose} data-testid="waitlist-modal-close">&times;</button>
        {submitted ? (
          <div className="ctfd-form-success" data-testid="waitlist-success">
            <p className="ctfd-success-msg">You are on the list. The Protocol is coming. You will be the first to know.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="waitlist-form">
            <h3 className="ctfd-modal-title">Join the Waitlist</h3>
            <p className="ctfd-modal-subtitle">Be the first to get the Protocol when it drops.</p>
            <div className="ctfd-field">
              <label>First Name</label>
              <input type="text" value={firstName} onChange={e => { setFirstName(e.target.value); setErrors(p => ({...p, first_name: ''})); }} data-testid="waitlist-first-name" />
              {errors.first_name && <span className="ctfd-field-error">{errors.first_name}</span>}
            </div>
            <div className="ctfd-field">
              <label>Email Address</label>
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setErrors(p => ({...p, email: ''})); }} data-testid="waitlist-email" />
              {errors.email && <span className="ctfd-field-error">{errors.email}</span>}
            </div>
            <button type="submit" className="ctfd-btn ctfd-btn-outline ctfd-btn-block" disabled={loading} data-testid="waitlist-submit">
              {loading ? 'Submitting...' : 'Join the Waitlist'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ApplicationModal({ onClose, defaultTier }) {
  const [form, setForm] = useState({
    tier: defaultTier || 'Cohort ($1,500)',
    first_name: '', last_name: '', email: '', phone: '', phone_country: 'US', website: '', social_media: '',
    q1_why_calm: '', q2_extra_calm: '', q3_why_now: '', q4_world_benefit: ''
  });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => { setForm(p => ({...p, [k]: e.target.value})); setErrors(p => ({...p, [k]: ''})); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'Required';
    if (!form.last_name.trim()) errs.last_name = 'Required';
    if (!form.email.trim()) errs.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email';
    if (!form.phone.trim()) errs.phone = 'Required';
    else if (!isValidPhoneNumber(form.phone, form.phone_country)) errs.phone = 'Enter a valid phone number';
    if (!form.q1_why_calm.trim()) errs.q1_why_calm = 'Required';
    if (!form.q2_extra_calm.trim()) errs.q2_extra_calm = 'Required';
    if (!form.q3_why_now.trim()) errs.q3_why_now = 'Required';
    if (!form.q4_world_benefit.trim()) errs.q4_world_benefit = 'Required';
    if (Object.keys(errs).length) {
      setErrors(errs);
      const labels = {
        first_name: 'First Name', last_name: 'Last Name', email: 'Email Address',
        phone: 'Phone Number', q1_why_calm: 'Question 1', q2_extra_calm: 'Question 2',
        q3_why_now: 'Question 3', q4_world_benefit: 'Question 4'
      };
      const missing = Object.entries(errs).map(([k, v]) => `${labels[k] || k}: ${v}`);
      alert(`Please fix the following:\n\n${missing.join('\n')}`);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ctfd/application`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: form.tier, first_name: form.first_name.trim(), last_name: form.last_name.trim(),
          email: form.email.trim(), phone: form.phone.trim(), website: form.website.trim(), social_media: form.social_media.trim(),
          q1_why_calm: form.q1_why_calm.trim(), q2_extra_calm: form.q2_extra_calm.trim(),
          q3_why_now: form.q3_why_now.trim(), q4_world_benefit: form.q4_world_benefit.trim()
        })
      });
      if (res.ok) setSubmitted(true);
    } catch { /* noop */ }
    setLoading(false);
  };

  return (
    <div className="ctfd-modal-overlay" onClick={onClose} data-testid="application-modal-overlay">
      <div className="ctfd-modal ctfd-modal-lg" onClick={e => e.stopPropagation()} data-testid="application-modal">
        <button className="ctfd-modal-close" onClick={onClose} data-testid="application-modal-close">&times;</button>
        {submitted ? (
          <div className="ctfd-form-success" data-testid="application-success">
            <p className="ctfd-success-msg">Thank you for submitting. Please book your interview below.</p>
            <a href="https://app.usemotion.com/meet/marco-hansell/VIP?d=15" target="_blank" rel="noopener noreferrer" className="ctfd-btn ctfd-btn-primary" style={{ marginTop: 24, display: 'inline-block' }} data-testid="book-interview-btn">Book your CTFD Interview Here</a>
          </div>
        ) : (
          <form onSubmit={handleSubmit} data-testid="application-form">
            <h3 className="ctfd-modal-title">Apply</h3>
            <p className="ctfd-modal-subtitle">Tell me a bit about where you are and why this matters to you right now.</p>
            <div className="ctfd-field">
              <label>Which tier are you applying for?</label>
              <select value={form.tier} onChange={set('tier')} data-testid="application-tier">
                <option>Cohort ($1,500)</option>
                <option>Implementation ($5,000)</option>
              </select>
            </div>
            <div className="ctfd-field-row">
              <div className="ctfd-field">
                <label>First Name</label>
                <input type="text" value={form.first_name} onChange={set('first_name')} data-testid="application-first-name" />
                {errors.first_name && <span className="ctfd-field-error">{errors.first_name}</span>}
              </div>
              <div className="ctfd-field">
                <label>Last Name</label>
                <input type="text" value={form.last_name} onChange={set('last_name')} data-testid="application-last-name" />
                {errors.last_name && <span className="ctfd-field-error">{errors.last_name}</span>}
              </div>
            </div>
            <div className="ctfd-field">
              <label>Email Address</label>
              <input type="email" value={form.email} onChange={set('email')} data-testid="application-email" />
              {errors.email && <span className="ctfd-field-error">{errors.email}</span>}
            </div>
            <div className="ctfd-field">
              <label>Phone Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={form.phone_country} onChange={set('phone_country')} style={{ width: 100, flexShrink: 0 }} data-testid="application-phone-country">
                  {['US','CA','GB','AU','DE','FR','IN','BR','MX','JP','KR','IT','ES','NL','SE','CH','IE','NZ','SG','HK','IL','AE','ZA','NG','KE','GH','PH','CO','AR','CL','PT','BE','AT','DK','NO','FI','PL','CZ','RO','HU','GR','TR','RU','UA','EG','SA','QA','KW','BH','JO','LB','PK','BD','LK','MY','TH','VN','ID','TW','CN'].map(c => (
                    <option key={c} value={c}>+{getCountryCallingCode(c)} {c}</option>
                  ))}
                </select>
                <input type="tel" placeholder="(555) 123-4567" value={form.phone} onChange={set('phone')} style={{ flex: 1 }} data-testid="application-phone" />
              </div>
              {errors.phone && <span className="ctfd-field-error">{errors.phone}</span>}
            </div>
            <div className="ctfd-field-row">
              <div className="ctfd-field">
                <label>Website <span className="ctfd-optional">(optional)</span></label>
                <input type="text" value={form.website} onChange={set('website')} data-testid="application-website" />
              </div>
              <div className="ctfd-field">
                <label>Social Media <span className="ctfd-optional">(optional)</span></label>
                <input type="text" placeholder="@instagram" value={form.social_media} onChange={set('social_media')} data-testid="application-social" />
              </div>
            </div>
            <div className="ctfd-field">
              <label>Why do you need to calm the f*ck down in your life?</label>
              <textarea rows={4} value={form.q1_why_calm} onChange={set('q1_why_calm')} data-testid="application-q1" />
              {errors.q1_why_calm && <span className="ctfd-field-error">{errors.q1_why_calm}</span>}
            </div>
            <div className="ctfd-field">
              <label>What do you want to do with all that extra calm you gain?</label>
              <textarea rows={4} value={form.q2_extra_calm} onChange={set('q2_extra_calm')} data-testid="application-q2" />
              {errors.q2_extra_calm && <span className="ctfd-field-error">{errors.q2_extra_calm}</span>}
            </div>
            <div className="ctfd-field">
              <label>Why is it important to calm the f*ck down now?</label>
              <textarea rows={4} value={form.q3_why_now} onChange={set('q3_why_now')} data-testid="application-q3" />
              {errors.q3_why_now && <span className="ctfd-field-error">{errors.q3_why_now}</span>}
            </div>
            <div className="ctfd-field">
              <label>How will the world benefit from you being a calmer version of yourself as a leader?</label>
              <textarea rows={4} value={form.q4_world_benefit} onChange={set('q4_world_benefit')} data-testid="application-q4" />
              {errors.q4_world_benefit && <span className="ctfd-field-error">{errors.q4_world_benefit}</span>}
            </div>
            <button type="submit" className="ctfd-btn ctfd-btn-primary ctfd-btn-block" disabled={loading} data-testid="application-submit">
              {loading ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function CTFDProtocol() {
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showApplication, setShowApplication] = useState(false);
  const [appTier, setAppTier] = useState('Cohort ($1,500)');

  const openApp = (tier) => { setAppTier(tier); setShowApplication(true); };

  useEffect(() => {
    // Intersection observer for fade-in sections
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) entry.target.classList.add('ctfd-visible');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.ctfd-fade-in').forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const toggleFaq = (e) => {
    const item = e.currentTarget.parentElement;
    const wasOpen = item.classList.contains('ctfd-open');
    document.querySelectorAll('.ctfd-faq-item').forEach(i => i.classList.remove('ctfd-open'));
    if (!wasOpen) item.classList.add('ctfd-open');
  };

  return (
    <div className="ctfd-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&family=Instrument+Serif:ital@0;1&family=Caveat:wght@400;500;600;700&display=swap');

        .ctfd-page {
          --ctfd-bg: #FAFAF8;
          --ctfd-text: #1A1A1A;
          --ctfd-text-mid: #555;
          --ctfd-text-light: #888;
          --ctfd-accent: #E8590C;
          --ctfd-accent-hover: #D14B08;
          --ctfd-card-bg: #F2F1EE;
          --ctfd-card-border: #E0DFDB;
          --ctfd-vip-bg: #1A1A1A;
          --ctfd-vip-text: #FAFAF8;
          --ctfd-serif: 'Instrument Serif', Georgia, serif;
          --ctfd-sans: 'DM Sans', -apple-system, sans-serif;
          --ctfd-hand: 'Caveat', cursive;
          --ctfd-max-w: 960px;
          --ctfd-space-xs: 8px;
          --ctfd-space-sm: 16px;
          --ctfd-space-md: 32px;
          --ctfd-space-lg: 32px;
          --ctfd-space-xl: 48px;
          --ctfd-space-2xl: 64px;

          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          overflow-y: auto;
          overflow-x: hidden;
          z-index: 9999;
          min-height: 100vh;
          font-family: var(--ctfd-sans) !important;
          font-size: 17px;
          line-height: 1.7;
          color: var(--ctfd-text);
          background: var(--ctfd-bg);
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          -webkit-overflow-scrolling: touch;
        }

        .ctfd-page *, .ctfd-page *::before, .ctfd-page *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .ctfd-container { max-width: var(--ctfd-max-w); margin: 0 auto; padding: 0 24px; }

        .ctfd-section { padding: var(--ctfd-space-xl) 0; }
        @media (max-width: 600px) {
          .ctfd-section { padding: var(--ctfd-space-lg) 0; }
          .ctfd-container { padding: 0 20px; }
        }

        .ctfd-page h1 {
          font-family: var(--ctfd-sans);
          font-size: clamp(2.4rem, 8vw, 3.8rem);
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: -0.03em;
          color: var(--ctfd-text);
          margin: 0;
        }

        .ctfd-page h2 {
          font-family: var(--ctfd-serif);
          font-size: clamp(1.8rem, 5vw, 2.6rem);
          font-weight: 400;
          line-height: 1.2;
          letter-spacing: -0.01em;
          margin-bottom: var(--ctfd-space-md);
          color: var(--ctfd-text);
        }

        .ctfd-page h3 {
          font-family: var(--ctfd-sans);
          font-size: 1.15rem;
          font-weight: 600;
          line-height: 1.4;
          letter-spacing: -0.01em;
          margin-bottom: var(--ctfd-space-xs);
        }

        .ctfd-page p { margin-bottom: 1.2em; color: var(--ctfd-text); }
        .ctfd-page p:last-child { margin-bottom: 0; }

        .ctfd-text-mid { color: var(--ctfd-text-mid) !important; }
        .ctfd-text-light { color: var(--ctfd-text-light) !important; }

        .ctfd-lead { font-size: clamp(1.1rem, 3vw, 1.3rem); line-height: 1.65; }

        .ctfd-loud-line {
          font-size: clamp(1.15rem, 3vw, 1.35rem);
          font-weight: 500;
          line-height: 1.6;
          margin-bottom: 0.4em;
        }

        .ctfd-stacked-lines { margin-bottom: 1.5em; }
        .ctfd-stacked-lines .ctfd-line {
          display: block;
          font-size: clamp(1.05rem, 2.5vw, 1.2rem);
          line-height: 1.8;
        }

        .ctfd-redacted {
          position: relative;
          display: inline-block;
          background: var(--ctfd-text);
          color: var(--ctfd-bg);
          padding: 2px 10px;
          margin: 0 4px;
          border-radius: 2px;
          transform: rotate(-0.3deg);
        }

        .ctfd-hand-note {
          font-family: var(--ctfd-hand);
          font-size: 1.25rem;
          color: var(--ctfd-accent);
          transform: rotate(-2deg);
          display: inline-block;
          line-height: 1.4;
        }

        .ctfd-hand-note-right {
          font-family: var(--ctfd-hand);
          font-size: 1.2rem;
          color: var(--ctfd-accent);
          transform: rotate(-1.5deg);
          display: inline-block;
          line-height: 1.4;
        }

        .ctfd-hand-underline {
          position: relative;
          display: inline;
        }
        .ctfd-hand-underline::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: -2px;
          right: -2px;
          height: 8px;
          background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 12' preserveAspectRatio='none'%3E%3Cpath d='M2 8 Q30 2, 60 7 T120 5 T180 8 L198 6' fill='none' stroke='%23E8590C' stroke-width='2.5' stroke-linecap='round'/%3E%3C/svg%3E") no-repeat;
          background-size: 100% 100%;
        }

        .ctfd-divider-hand {
          width: 100%;
          max-width: 120px;
          height: 20px;
          margin: var(--ctfd-space-md) 0;
          opacity: 0.35;
        }
        @media (max-width: 600px) { .ctfd-divider-hand { margin: var(--ctfd-space-sm) 0; } }

        .ctfd-type-blow {
          font-family: var(--ctfd-sans);
          font-size: clamp(2.8rem, 10vw, 5rem);
          font-weight: 700;
          line-height: 1.0;
          letter-spacing: -0.04em;
          color: var(--ctfd-text);
          margin: var(--ctfd-space-md) 0;
        }

        .ctfd-type-blow-accent {
          font-family: var(--ctfd-sans);
          font-size: clamp(2.4rem, 8vw, 4rem);
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: -0.03em;
          color: var(--ctfd-accent);
          margin: var(--ctfd-space-sm) 0;
        }

        .ctfd-type-blow-serif {
          font-family: var(--ctfd-serif);
          font-size: clamp(2.6rem, 9vw, 4.5rem);
          font-weight: 400;
          font-style: italic;
          line-height: 1.05;
          letter-spacing: -0.02em;
          color: var(--ctfd-text);
          margin: var(--ctfd-space-md) 0;
        }

        .ctfd-btn {
          display: inline-block;
          font-family: var(--ctfd-sans);
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.01em;
          padding: 16px 36px;
          border-radius: 6px;
          text-decoration: none;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          text-align: center;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation;
        }
        @media (max-width: 600px) {
          .ctfd-btn { padding: 14px 28px; font-size: 0.95rem; }
          .ctfd-hero-cta { flex-direction: column; }
          .ctfd-hero-cta .ctfd-btn { width: 100%; }
        }
        .ctfd-btn-primary { background: var(--ctfd-accent); color: #fff; }
        .ctfd-btn-primary:hover { background: var(--ctfd-accent-hover); transform: translateY(-1px); }
        .ctfd-btn-outline { background: transparent; color: var(--ctfd-text); border: 2px solid var(--ctfd-text); }
        .ctfd-btn-outline:hover { background: var(--ctfd-text); color: var(--ctfd-bg); }
        .ctfd-btn-white { background: #fff; color: var(--ctfd-text); }
        .ctfd-btn-white:hover { background: #f0f0f0; transform: translateY(-1px); }
        .ctfd-btn-block { display: block; width: 100%; }

        .ctfd-hero { padding: var(--ctfd-space-xl) 0 var(--ctfd-space-lg); text-align: left; }
        @media (max-width: 600px) { .ctfd-hero { padding: var(--ctfd-space-lg) 0 var(--ctfd-space-md); } }
        .ctfd-hero-label {
          display: inline-block;
          font-size: 0.8rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--ctfd-accent);
          margin-bottom: var(--ctfd-space-sm);
        }
        .ctfd-hero h1 { margin-bottom: var(--ctfd-space-md); }
        .ctfd-hero .ctfd-lead { max-width: 560px; }
        .ctfd-hero-cta { margin-top: var(--ctfd-space-md); display: flex; gap: 12px; flex-wrap: wrap; }

        .ctfd-trap-block {
          font-family: var(--ctfd-serif);
          font-size: clamp(1.3rem, 4vw, 1.7rem);
          font-style: italic;
          line-height: 1.5;
          color: var(--ctfd-text);
          padding: var(--ctfd-space-md) 0;
          border-left: 3px solid var(--ctfd-accent);
          padding-left: var(--ctfd-space-md);
          margin: var(--ctfd-space-md) 0;
        }

        .ctfd-week-grid { display: grid; gap: 16px; }
        .ctfd-week-card {
          background: var(--ctfd-card-bg);
          border-radius: 10px;
          padding: 28px;
          display: flex;
          gap: 20px;
          align-items: flex-start;
        }
        @media (max-width: 600px) {
          .ctfd-week-card { padding: 20px; gap: 14px; flex-direction: column; }
          .ctfd-week-num { min-width: auto; }
        }
        .ctfd-week-num {
          font-family: var(--ctfd-sans);
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: var(--ctfd-accent);
          white-space: nowrap;
          padding-top: 2px;
          min-width: 52px;
        }
        .ctfd-week-card h3 { margin-bottom: 4px; }
        .ctfd-week-card p { font-size: 0.95rem; color: var(--ctfd-text-mid); margin-bottom: 0; }

        .ctfd-pricing-grid { display: grid; gap: 20px; margin-top: var(--ctfd-space-md); }
        @media (min-width: 601px) { .ctfd-pricing-grid { grid-template-columns: repeat(3, 1fr); } }
        .ctfd-price-card {
          background: var(--ctfd-card-bg);
          border: 1px solid var(--ctfd-card-border);
          border-radius: 12px;
          padding: 32px 28px;
          position: relative;
          transition: transform 0.15s ease;
        }
        @media (max-width: 600px) { .ctfd-price-card { padding: 24px 20px; } }
        .ctfd-price-card:hover { transform: translateY(-2px); }
        .ctfd-price-card.ctfd-featured { border: 2px solid var(--ctfd-accent); background: #fff; }
        .ctfd-price-card.ctfd-vip { background: var(--ctfd-vip-bg); color: var(--ctfd-vip-text); border-color: var(--ctfd-vip-bg); }
        .ctfd-price-card.ctfd-vip .ctfd-text-mid, .ctfd-price-card.ctfd-vip p { color: #bbb; }
        .ctfd-price-card.ctfd-vip .ctfd-price { color: var(--ctfd-vip-text); }

        .ctfd-price-tier {
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 4px;
        }
        .ctfd-price-card.ctfd-featured .ctfd-price-tier { color: var(--ctfd-accent); }
        .ctfd-price-card.ctfd-vip .ctfd-price-tier { color: var(--ctfd-accent); }
        .ctfd-price-subtitle { font-size: 0.95rem; color: var(--ctfd-text-mid); margin-bottom: var(--ctfd-space-sm); }
        .ctfd-price {
          font-size: 2.8rem;
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1;
          margin-bottom: var(--ctfd-space-sm);
        }
        .ctfd-price-includes { list-style: none; padding: 0; margin-bottom: 24px; }
        .ctfd-price-includes li {
          font-size: 0.93rem;
          padding: 5px 0;
          position: relative;
          padding-left: 20px;
          color: var(--ctfd-text-mid);
        }
        .ctfd-price-includes li::before {
          content: '\\2713';
          position: absolute; left: 0;
          font-weight: 700;
          color: var(--ctfd-accent);
        }
        .ctfd-price-card.ctfd-vip .ctfd-price-includes li { color: #bbb; }
        .ctfd-price-note {
          font-size: 0.9rem;
          font-style: italic;
          color: var(--ctfd-text-mid);
          margin-top: 16px;
          line-height: 1.55;
        }
        .ctfd-price-card.ctfd-vip .ctfd-price-note { color: #999; }
        .ctfd-tag-limited {
          display: inline-block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          background: var(--ctfd-accent);
          color: #fff;
          padding: 4px 10px;
          border-radius: 4px;
          margin-bottom: 12px;
        }

        .ctfd-faq-item { border-bottom: 1px solid var(--ctfd-card-border); padding: 24px 0; }
        .ctfd-faq-item:last-child { border-bottom: none; }
        .ctfd-faq-q {
          font-weight: 600;
          font-size: 1.05rem;
          margin-bottom: 8px;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
        }
        .ctfd-faq-toggle { width: 24px; height: 24px; flex-shrink: 0; position: relative; }
        .ctfd-faq-toggle::before, .ctfd-faq-toggle::after {
          content: '';
          position: absolute;
          background: var(--ctfd-text-light);
          transition: transform 0.25s ease, opacity 0.25s ease;
        }
        .ctfd-faq-toggle::before { width: 16px; height: 2px; top: 50%; left: 50%; transform: translate(-50%, -50%); }
        .ctfd-faq-toggle::after { width: 2px; height: 16px; top: 50%; left: 50%; transform: translate(-50%, -50%); }
        .ctfd-faq-item.ctfd-open .ctfd-faq-toggle::after { transform: translate(-50%, -50%) rotate(90deg); opacity: 0; }
        .ctfd-faq-a {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
          color: var(--ctfd-text-mid);
          line-height: 1.65;
          font-size: 0.98rem;
        }
        .ctfd-faq-item.ctfd-open .ctfd-faq-a { max-height: 300px; }

        .ctfd-final-cta { text-align: center; padding: var(--ctfd-space-xl) 0 var(--ctfd-space-2xl); }
        .ctfd-final-cta-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 12px;
          max-width: 520px;
          margin: 0 auto;
        }
        @media (max-width: 600px) { .ctfd-final-cta-grid { grid-template-columns: 1fr; max-width: 320px; } }
        .ctfd-final-cta-option { text-align: center; }
        .ctfd-final-cta-option .ctfd-tier-name { font-size: 0.78rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 2px; }
        .ctfd-final-cta-option .ctfd-tier-price { font-size: 1.6rem; font-weight: 700; margin-bottom: 4px; }
        .ctfd-final-cta-option .ctfd-tier-desc { font-size: 0.82rem; color: var(--ctfd-text-mid); margin-bottom: 12px; }

        .ctfd-section-label {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--ctfd-text-light);
          margin-bottom: var(--ctfd-space-sm);
        }

        .ctfd-footer {
          text-align: center;
          padding: var(--ctfd-space-md) 0;
          font-size: 0.85rem;
          color: var(--ctfd-text-light);
          border-top: 1px solid var(--ctfd-card-border);
        }
        .ctfd-footer a { color: var(--ctfd-text-light); text-decoration: underline; }

        .ctfd-fade-in {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .ctfd-fade-in.ctfd-visible { opacity: 1; transform: translateY(0); }

        /* MODAL & FORM STYLES */
        .ctfd-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: ctfd-fade 0.2s ease;
        }
        @keyframes ctfd-fade { from { opacity: 0; } to { opacity: 1; } }
        .ctfd-modal {
          background: #fff;
          border-radius: 12px;
          padding: 40px 36px;
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
          animation: ctfd-slide 0.25s ease;
        }
        .ctfd-modal-lg { max-width: 560px; }
        @keyframes ctfd-slide { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @media (max-width: 600px) { .ctfd-modal { padding: 28px 20px; max-height: 92vh; } }
        .ctfd-modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          font-size: 28px;
          color: var(--ctfd-text-light);
          cursor: pointer;
          line-height: 1;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
        }
        .ctfd-modal-close:hover { background: var(--ctfd-card-bg); color: var(--ctfd-text); }
        .ctfd-modal-title {
          font-family: var(--ctfd-sans) !important;
          font-size: 1.4rem;
          font-weight: 700;
          margin-bottom: 4px;
        }
        .ctfd-modal-subtitle {
          font-size: 0.95rem;
          color: var(--ctfd-text-mid);
          margin-bottom: 24px;
        }
        .ctfd-field { margin-bottom: 18px; }
        .ctfd-field label {
          display: block;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 6px;
          color: var(--ctfd-text);
        }
        .ctfd-optional { font-weight: 400; color: var(--ctfd-text-light); font-size: 0.82rem; }
        .ctfd-field input, .ctfd-field select, .ctfd-field textarea {
          width: 100%;
          font-family: var(--ctfd-sans) !important;
          font-size: 0.95rem;
          padding: 12px 14px;
          border: 1px solid #E0DFDB;
          border-radius: 6px;
          background: #F2F1EE;
          color: var(--ctfd-text);
          outline: none;
          transition: border-color 0.15s;
        }
        .ctfd-field input:focus, .ctfd-field select:focus, .ctfd-field textarea:focus { border-color: var(--ctfd-accent); background: #fff; }
        .ctfd-field textarea { resize: vertical; min-height: 80px; }
        .ctfd-field select { cursor: pointer; }
        .ctfd-field-error { display: block; font-size: 0.82rem; color: #D32F2F; margin-top: 4px; }
        .ctfd-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 600px) { .ctfd-field-row { grid-template-columns: 1fr; } }
        .ctfd-form-success { text-align: center; padding: 32px 0; }
        .ctfd-success-msg {
          font-family: var(--ctfd-serif) !important;
          font-size: 1.3rem;
          font-style: italic;
          line-height: 1.5;
          color: var(--ctfd-text);
        }
      `}</style>

      {/* HERO */}
      <section className="ctfd-hero">
        <div className="ctfd-container">
          <span className="ctfd-hero-label">Pilot cohort &middot; July 2026 &middot; Limited seats</span>
          <h1>Calm the<br /><span className="ctfd-redacted">F*ck</span> Down.</h1>
          <p className="ctfd-lead" style={{ marginTop: 20, maxWidth: 540 }}>A 30-day cohort for leaders who want to take back control of their pace, their nervous system, and their relationship with technology.</p>
          <div className="ctfd-hero-cta">
            <button type="button" onClick={() => openApp('Cohort ($1,500)')} className="ctfd-btn ctfd-btn-primary" data-testid="hero-join-btn">Join the Pilot</button>
            <a href="/ctfdprotocol/quiz" className="ctfd-btn ctfd-btn-outline" data-testid="hero-calculate-btn">Calculate your Success Tax</a>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section id="ctfd-the-problem" className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-loud-line">You are not falling behind.</p>
          <p className="ctfd-text-mid">But it feels like it. Every morning there is a new tool, a new model, a new thing you should be integrating. You are struggling to figure out your AI strategy with so many new tools coming daily. You are making more decisions than ever, with more inputs than you can process, and the ground keeps shifting.</p>
          <p className="ctfd-loud-line" style={{ marginTop: 28 }}>And the answer everyone is selling you is: move faster.</p>
          <p className="ctfd-text-mid">More tools. More output. More optimization. More.</p>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 12 Q15 4, 30 11 T60 9 T90 12 T118 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* WHAT IF */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-type-blow">What if the answer<br />is the opposite?</p>
          <p className="ctfd-hand-note" style={{ marginTop: 8, marginBottom: 28 }}>sit with that for a second.</p>
          <p className="ctfd-loud-line">What if the leaders who slow down first are the ones who end up ahead?</p>
          <p className="ctfd-text-mid">What if a calmer nervous system makes you a sharper decision-maker? What if the signal you are looking for only shows up when you turn the noise down?</p>
          <p className="ctfd-loud-line"><span className="ctfd-hand-underline">What if it means you finally catch up to the life you have been running past?</span></p>
          <p className="ctfd-hand-note" style={{ marginTop: 4 }}>that is the real edge.</p>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 10 Q20 16, 40 8 T75 12 T110 7 L118 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* TECH PROMISE */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <div className="ctfd-trap-block">Since the 1930s, every major technological innovation has promised us more free time. We are still working the same hours. We are less calm than ever. AI is about to accelerate that pattern unless someone breaks it.</div>
          <p className="ctfd-loud-line">That someone is you.</p>
          <p className="ctfd-text-mid">You set the pace. For your team, your company, your family. If your nervous system is in overdrive, everything you build carries that energy.</p>
          <p>If you calm down first, everything changes.</p>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 14 Q25 5, 45 12 T80 8 T118 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* WHAT THIS IS */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-section-label">What this is</p>
          <h2>A pace reset for leaders.</h2>
          <p className="ctfd-text-mid">This is not a productivity course. This is not "how to use AI better." This is not a wellness retreat.</p>
          <p className="ctfd-loud-line">This is a room full of leaders who have decided to stop racing and start choosing.</p>
          <p className="ctfd-text-mid">Over four weeks, we reset your relationship with technology. Starting with you. Your nervous system. Your rules. Your pace. Then we bring in the tools, the AI, the systems, calibrated to serve your life instead of consume it.</p>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 8 Q18 15, 38 7 T70 13 T105 8 L118 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* THE 4 WEEKS */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-section-label">The four weeks</p>
          <h2>Human first. Technology second.</h2>
          <div className="ctfd-week-grid">
            <div className="ctfd-week-card">
              <span className="ctfd-week-num">Week 1</span>
              <div>
                <h3>Calm the Noise</h3>
                <p>Audit what is actually running your days. Figure out the gap between where your rhythm is and where you actually want it to be. Start turning the noise down.</p>
              </div>
            </div>
            <div className="ctfd-week-card">
              <span className="ctfd-week-num">Week 2</span>
              <div>
                <h3>Install the Rules</h3>
                <p>Build simple decision rules for your schedule, your consumption, your communication. Stop making the same choices over and over from scratch.</p>
              </div>
            </div>
            <div className="ctfd-week-card">
              <span className="ctfd-week-num">Week 3</span>
              <div>
                <h3>Reclaim the Space</h3>
                <p>Use the rules to compress work, protect time, and create actual breathing room. Design what "enough" looks like for you.</p>
              </div>
            </div>
            <div className="ctfd-week-card">
              <span className="ctfd-week-num">Week 4</span>
              <div>
                <h3>Put Technology in Its Place</h3>
                <p>Now bring AI in. Set up tools that serve your new pace. Build your personal AI system around your values, not just your tasks. Learn to lead your team with the same philosophy.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 12 Q22 4, 42 11 T78 7 T118 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* WHO THIS IS FOR */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-section-label">Who this is for</p>
          <h2>Leaders who feel the weight.</h2>
          <p className="ctfd-text-mid">You make decisions that affect other people. You are building in a world that is changing faster than you can process. And something in you knows that just going faster is not the answer.</p>
          <p className="ctfd-loud-line" style={{ marginTop: 20 }}>You might be feeling:</p>
          <div className="ctfd-stacked-lines" style={{ marginTop: 12 }}>
            <span className="ctfd-line ctfd-text-mid">Behind, even though you are doing more than ever.</span>
            <span className="ctfd-line ctfd-text-mid">Pressure to AI-ify everything before you lose your edge.</span>
            <span className="ctfd-line ctfd-text-mid">A gap between how fast you are moving and how well you are living.</span>
            <span className="ctfd-line ctfd-text-mid">Concern about what this pace is doing to the people around you.</span>
          </div>
          <p className="ctfd-loud-line" style={{ marginTop: 28 }}>And underneath all of that:</p>
          <p className="ctfd-text-mid">You want to slow down without falling behind. You want to make fewer decisions without losing your edge. You want to know that it is safe to stop running.</p>
          <p>It is.</p>
          <p className="ctfd-hand-note" style={{ marginTop: 4 }}>that is the whole point of this room.</p>
          <p className="ctfd-text-mid" style={{ marginTop: 20 }}>This is for people who want to lead at the pace of true change, not just optimization and extraction.</p>
          <p className="ctfd-hand-note" style={{ marginTop: 4 }}>there is a difference.</p>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 10 Q28 16, 50 8 T85 13 T118 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* YOUR FACILITATOR */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-section-label">Your facilitator</p>
          <h2>I had everything moving fast and nothing moving well.</h2>
          <p className="ctfd-text-mid">I am Marco Hansell. I founded Speakr and helped build the influencer marketing industry. I moved millions of dollars. I also lost $35 million and burned out chasing a number that was never going to be enough.</p>
          <p className="ctfd-text-mid">What changed my life was not a better system. It was learning to calm down. Less input, more clarity. Less pressure, more presence. Less speed, more life.</p>
          <p className="ctfd-loud-line">Now I help leaders do the same thing, with better tools and less pain than I had.</p>
          <p className="ctfd-text-mid">I work at the intersection of human systems and technology. I build decision frameworks, AI-integrated operating systems, and containers that help high-agency people stop reacting and start choosing.</p>
          <p className="ctfd-text-mid">This cohort is what I wish someone had offered me when I was moving at a pace that was quietly destroying everything that mattered.</p>
          <p>The life on the other side of calming down is not smaller. It is actually bigger. Just quieter. And yours.</p>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 14 Q20 6, 40 12 T72 7 T118 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* TESTIMONIALS */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-section-label">What people say about working with Marco</p>
          <h2>The room matters. So does who runs it.</h2>
          <div className="ctfd-week-grid">
            <div className="ctfd-week-card" style={{ display: 'block' }}>
              <p style={{ fontFamily: "var(--ctfd-serif)", fontStyle: 'italic', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: 16 }}>{`\u201C`}The leadership I experienced in this challenge that Marco was leading was really powerful. A really powerful way of looking at the next 90 days of my life. Thank you for showing me more possibilities than I was seeing at the time.{`\u201D`}</p>
              <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>Quddus Phillip</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--ctfd-text-light)' }}>Former MTV Host of TRL, CEO of Sound Sanctuary</p>
            </div>
            <div className="ctfd-week-card" style={{ display: 'block' }}>
              <p style={{ fontFamily: "var(--ctfd-serif)", fontStyle: 'italic', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: 16 }}>{`\u201C`}Marco is a transformer. To be in his presence, physically or virtually, is to be transformed by his presence. I love how he sees the world. He is one of us, demonstrating our power to conjure better stories for all of us to live, for real.{`\u201D`}</p>
              <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>Baratunde Thurston</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--ctfd-text-light)' }}>Emmy-Nominated Author of NY Times Bestseller &ldquo;How to Be Black&rdquo;</p>
            </div>
            <div className="ctfd-week-card" style={{ display: 'block' }}>
              <p style={{ fontFamily: "var(--ctfd-serif)", fontStyle: 'italic', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: 16 }}>{`\u201C`}So much of my life was at an eight. But then why not take it to the next level and really be vulnerable and human and impactful in ways that I know I can and have been. And holy, 180 degrees in 40 days, really fundamentally shifting from operating with fear to operating with strength, confidence.{`\u201D`}</p>
              <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>B.H.</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--ctfd-text-light)' }}>CEO, $100M Real Estate Fund</p>
            </div>
            <div className="ctfd-week-card" style={{ display: 'block' }}>
              <p style={{ fontFamily: "var(--ctfd-serif)", fontStyle: 'italic', fontSize: '1.05rem', lineHeight: 1.6, marginBottom: 16 }}>{`\u201C`}I got my monies worth of value just from answering the intake questions. Before we even started.{`\u201D`}</p>
              <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 2 }}>E.B.</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--ctfd-text-light)' }}>Partner, SF Venture Capital Firm</p>
            </div>
          </div>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 10 Q22 16, 45 8 T80 14 T118 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* PRICING */}
      <section id="ctfd-pricing" className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-section-label">Join the pilot</p>
          <h2>Two ways in.</h2>
          <div className="ctfd-pricing-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
            <div className="ctfd-price-card ctfd-featured">
              <div className="ctfd-price-tier">Cohort</div>
              <div className="ctfd-price-subtitle">Done with you</div>
              <div className="ctfd-price">$1,500</div>
              <ul className="ctfd-price-includes">
                <li>4 weekly live sessions</li>
                <li>The CTFD Protocol and framework</li>
                <li>Private community for the cohort</li>
                <li>Decision rules, audits, and prompts</li>
                <li>AI relationship and tool guidance</li>
                <li>Shared accountability with other leaders</li>
              </ul>
              <button type="button" onClick={() => openApp('Cohort ($1,500)')} className="ctfd-btn ctfd-btn-primary ctfd-btn-block" data-testid="pricing-cohort-btn">Apply for Cohort</button>
              <p className="ctfd-price-note">You learn the system. You apply it to your life. You have a room of people doing it with you.</p>
              <p className="ctfd-hand-note-right" style={{ marginTop: 10, transform: 'rotate(-1deg)' }}>this is the one &larr;</p>
            </div>

            <div className="ctfd-price-card ctfd-vip">
              <span className="ctfd-tag-limited">Limited to 5</span>
              <div className="ctfd-price-tier">Implementation</div>
              <div className="ctfd-price-subtitle" style={{ color: '#999' }}>Done for you</div>
              <div className="ctfd-price">$5,000</div>
              <ul className="ctfd-price-includes">
                <li>Everything in Cohort</li>
                <li>Personal audit of your schedule, systems, and AI stack</li>
                <li>Custom rule design for your business and life</li>
                <li>Your own AI project built around your values and goals</li>
                <li>Direct implementation support throughout the 4 weeks</li>
              </ul>
              <button type="button" onClick={() => openApp('Implementation ($5,000)')} className="ctfd-btn ctfd-btn-white ctfd-btn-block" data-testid="pricing-implementation-btn">Apply for Implementation</button>
              <p className="ctfd-price-note">You get the system installed. Audit, setup, rules, AI configuration. Built for your actual life, not a template.</p>
            </div>
          </div>
          <p className="ctfd-text-mid" style={{ textAlign: 'center', marginTop: 32, fontSize: '0.95rem' }}>Not ready for the room yet? <button type="button" onClick={() => setShowWaitlist(true)} style={{ background: 'none', border: 'none', color: 'var(--ctfd-accent)', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--ctfd-sans)', fontSize: '0.95rem', padding: 0, textDecoration: 'underline' }} data-testid="pricing-waitlist-btn">Join the waitlist</button> for the self-guided Protocol.</p>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 9 Q15 15, 35 8 T65 13 T100 7 L118 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* WHAT YOU LEAVE WITH */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-section-label">After 4 weeks</p>
          <h2>You leave with a different pace.</h2>
          <p className="ctfd-text-mid">Not a slower business. Not fewer ambitions. Not a retreat from the world.</p>
          <p className="ctfd-loud-line">Imagine having clarity on how to move in this new world. Where your relationship with technology inspires you to live more, not scroll more. Where you feel confident in your decisions and you are setting that standard for the people around you.</p>
          <p className="ctfd-text-mid">That is what this builds toward.</p>
          <p className="ctfd-text-mid">You leave with more than a calmer nervous system. You leave with a revised sense of who you are. The version of you that can be calm, that chooses calm, and that leads from that place. Rules that protect your time. A relationship with technology that serves you. And the knowledge that slowing down gave you back everything that speed was taking.</p>
          <p className="ctfd-type-blow-serif" style={{ marginTop: 24 }}>Less panic. More clarity.<br />Less noise. More life.</p>
          <p className="ctfd-hand-note" style={{ marginTop: 8 }}>this is what it feels like on the other side.</p>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 11 Q25 5, 48 12 T82 8 T118 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* FAQ */}
      <section className="ctfd-section ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-section-label">FAQ</p>
          <h2>Questions.</h2>

          <div className="ctfd-faq-item ctfd-open">
            <div className="ctfd-faq-q" onClick={toggleFaq}>
              <span>When does it start?</span>
              <span className="ctfd-faq-toggle"></span>
            </div>
            <div className="ctfd-faq-a"><p>July 1, 2026. Exact date announced when spots are filled.</p></div>
          </div>

          <div className="ctfd-faq-item">
            <div className="ctfd-faq-q" onClick={toggleFaq}>
              <span>Why is there an application?</span>
              <span className="ctfd-faq-toggle"></span>
            </div>
            <div className="ctfd-faq-a"><p>This is a small room. The experience depends on who is in it. The application is short and exists to make sure this is the right fit for both of us.</p></div>
          </div>

          <div className="ctfd-faq-item">
            <div className="ctfd-faq-q" onClick={toggleFaq}>
              <span>Is this about AI?</span>
              <span className="ctfd-faq-toggle"></span>
            </div>
            <div className="ctfd-faq-a"><p>AI is part of the conversation, but the foundation is human. We start with your nervous system, your rules, your pace. Technology comes in as a tool in the second half, not the center.</p></div>
          </div>

          <div className="ctfd-faq-item">
            <div className="ctfd-faq-q" onClick={toggleFaq}>
              <span>Can I get the Protocol without joining the Cohort?</span>
              <span className="ctfd-faq-toggle"></span>
            </div>
            <div className="ctfd-faq-a"><p>Yes. The Protocol is not ready yet, but you can join the waitlist and you will be the first to get it when it drops. It is the self-guided version of the framework. The Cohort is where you implement it live with support and accountability.</p></div>
          </div>

          <div className="ctfd-faq-item">
            <div className="ctfd-faq-q" onClick={toggleFaq}>
              <span>What if I miss a live session?</span>
              <span className="ctfd-faq-toggle"></span>
            </div>
            <div className="ctfd-faq-a"><p>Replays are available. But the value is in the room, not the recording.</p></div>
          </div>

          <div className="ctfd-faq-item">
            <div className="ctfd-faq-q" onClick={toggleFaq}>
              <span>What is the difference between Cohort and Implementation?</span>
              <span className="ctfd-faq-toggle"></span>
            </div>
            <div className="ctfd-faq-a"><p>Cohort gives you the framework, the sessions, and the community. You apply it yourself. Implementation gives you all of that plus direct, hands-on help auditing your systems, building your rules, and setting up your AI stack. It is the difference between learning the system and having it installed.</p></div>
          </div>

          <div className="ctfd-faq-item">
            <div className="ctfd-faq-q" onClick={toggleFaq}>
              <span>Can I bring this to my team?</span>
              <span className="ctfd-faq-toggle"></span>
            </div>
            <div className="ctfd-faq-a"><p>This cohort is for the leader. What you learn here, you bring back. Team-level implementation is something we can explore after the pilot.</p></div>
          </div>

          <div className="ctfd-faq-item">
            <div className="ctfd-faq-q" onClick={toggleFaq}>
              <span>Is this a one-time thing?</span>
              <span className="ctfd-faq-toggle"></span>
            </div>
            <div className="ctfd-faq-a"><p>This is the pilot. If it works, there will be more. But this first room will not exist again in the same way.</p></div>
          </div>
        </div>
      </section>

      <div className="ctfd-container">
        <svg className="ctfd-divider-hand" viewBox="0 0 120 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M2 13 Q30 5, 55 12 T90 8 T118 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>

      {/* FINAL CTA */}
      <section className="ctfd-final-cta ctfd-fade-in">
        <div className="ctfd-container">
          <p className="ctfd-type-blow-serif">The pace you are running at<br />is a choice you can change.</p>
          <p className="ctfd-hand-note" style={{ marginTop: 8, marginBottom: 32 }}>start here.</p>
          <div className="ctfd-final-cta-grid">
            <div className="ctfd-final-cta-option">
              <div className="ctfd-tier-name">Protocol</div>
              <div className="ctfd-tier-price">Free</div>
              <div className="ctfd-tier-desc">Self-guided PDF</div>
              <button type="button" onClick={() => setShowWaitlist(true)} className="ctfd-btn ctfd-btn-outline ctfd-btn-block" data-testid="cta-waitlist-btn">Waitlist</button>
            </div>
            <div className="ctfd-final-cta-option">
              <div className="ctfd-tier-name" style={{ color: 'var(--ctfd-accent)' }}>Cohort</div>
              <div className="ctfd-tier-price">$1,500</div>
              <div className="ctfd-tier-desc">Done with you</div>
              <button type="button" onClick={() => openApp('Cohort ($1,500)')} className="ctfd-btn ctfd-btn-primary ctfd-btn-block" data-testid="cta-cohort-btn">Apply</button>
            </div>
            <div className="ctfd-final-cta-option">
              <div className="ctfd-tier-name">Implementation</div>
              <div className="ctfd-tier-price">$5,000</div>
              <div className="ctfd-tier-desc">Done for you</div>
              <button type="button" onClick={() => openApp('Implementation ($5,000)')} className="ctfd-btn ctfd-btn-outline ctfd-btn-block" data-testid="cta-implementation-btn">Apply</button>
            </div>
          </div>
        </div>
      </section>

      {/* MODALS */}
      {showWaitlist && <WaitlistModal onClose={() => setShowWaitlist(false)} />}
      {showApplication && <ApplicationModal onClose={() => setShowApplication(false)} defaultTier={appTier} />}

      {/* FOOTER */}
      <footer className="ctfd-footer">
        <div className="ctfd-container">
          <p>&copy; 2026 Calm the F*ck Down &middot; A <a href="https://thebinarygame.com">Binary Game</a> program &middot; Satori Media LLC</p>
        </div>
      </footer>
    </div>
  );
}
